import { useState, useCallback } from 'react';
import { API_BASE } from '../../../config/constants';
import { useDocs } from './HDocs';
import { User } from '../interfaces/IUsers';

export interface ObjectModel {
    id: string | number;
    name: string;
    imgUrl?: string;
    description?: string;
    [key: string]: any;
}

// use `User` from ../interfaces/IUsers

function filterObjectsByAssetClass(objects: ObjectModel[], filterValue: string): ObjectModel[] {
    const normalizedFilter = filterValue.trim().toLowerCase();
    if (normalizedFilter === '') {
        return objects;
    }

    const resolveAssetClass = (obj: ObjectModel) =>
        String(obj.assetclass ?? obj.asset_class ?? obj.assetClass ?? '')
            .toLowerCase()
            .trim();

    const hasAnyAssetClassValue = objects.some((obj) => resolveAssetClass(obj) !== '');
    if (!hasAnyAssetClassValue) {
        return objects;
    }

    return objects.filter((obj) => resolveAssetClass(obj) === normalizedFilter);
}

/**
 * Custom React hook to manage objects with user permission filtering.
 * Translates ObjectController.dart logic for React.
 *
 * Handles:
 * - Fetching all objects from API
 * - Filtering objects by user permissions
 * - Searching objects by name
 * - Sorting objects by name
 * - Creating, updating, deleting objects
 * - Managing selection state
 */
export function useObjectsTab() {
    const { uploadDocumentToAzure } = useDocs();

    const [allObjects, setAllObjects] = useState<ObjectModel[]>([]);
    const [filteredObjects, setFilteredObjects] = useState<ObjectModel[]>([]);
    const [selectedRows, setSelectedRows] = useState<boolean[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [currentUser] = useState<User | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [assetClassFilter, setAssetClassFilter] = useState<string>('RE');

    // fetchUserDetails removed (unused). Current user can be read with localStorage directly where needed.

    /**
     * Load all objects (asset-class filter only)
     */
    const loadAllObjects = useCallback(async (filterValue?: string): Promise<ObjectModel[]> => {
        setLoading(true);
        setError(null);

        const effectiveFilter =
            filterValue !== undefined ? filterValue.trim() : assetClassFilter.trim();
        setAssetClassFilter(effectiveFilter);

        try {
            // Fetch all objects
            const objectsRes = await fetch(`${API_BASE}/api/objects/get-all-objects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!objectsRes.ok) throw new Error('Failed to fetch objects');
            const objectsData = await objectsRes.json();

            const objects: ObjectModel[] = Array.isArray(objectsData)
                ? objectsData
                : Array.isArray(objectsData.data)
                ? objectsData.data
                : [];

            const assetClassFilteredObjects = filterObjectsByAssetClass(objects, effectiveFilter);

            setAllObjects(assetClassFilteredObjects);

            setFilteredObjects(assetClassFilteredObjects);
            setSelectedRows(new Array(assetClassFilteredObjects.length).fill(false));
            return assetClassFilteredObjects;
        } catch (err: any) {
            setError(err.message || 'Failed to load objects');
            setAllObjects([]);
            setFilteredObjects([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [assetClassFilter]);

    /**
     * Load only pending objects (approved === false)
     * Calls `loadAllObjects` and filters the results by the `approved` field.
     */
    const loadPendingObjects = useCallback(async (filterValue?: string): Promise<ObjectModel[]> => {
        setLoading(true);
        setError(null);
        try {
            const all = await loadAllObjects(filterValue);

            const pending = (all || []).filter((obj) => {
                const val = obj?.approved;
                const isApproved =val === 1;

                // anything that is not an explicit approval is considered pending
                return !isApproved;
            });

            setAllObjects(pending);
            setFilteredObjects(pending);
            setSelectedRows(new Array(pending.length).fill(false));

            return pending;
        } catch (err: any) {
            setError(err?.message || 'Failed to load pending objects');
            setAllObjects([]);
            setFilteredObjects([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [loadAllObjects]);

    /**
     * Load objects visible to a user.
     * Includes objects where `approved === true` OR where `object.company` matches `user.company`.
     */
    const loadUserObjects = useCallback(
        async (user?: User | null, filterValue?: string): Promise<ObjectModel[]> => {
            setLoading(true);
            setError(null);
            try {
                const all = await loadAllObjects(filterValue);

                const userCompany = user?.company ? String(user.company).toLowerCase().trim() : null;

                const results = (all || []).filter((obj) => {
                    const approvedVal = obj?.approved;
                    const isApproved =
                        approvedVal === true ||
                        approvedVal === 1 ||
                        approvedVal === '1' ||
                        (typeof approvedVal === 'string' && approvedVal.toLowerCase() === 'true');

                    const objCompany = obj?.company ? String(obj.company).toLowerCase().trim() : null;
                    const sameCompany = userCompany && objCompany && userCompany === objCompany;

                    return Boolean(isApproved || sameCompany);
                });

                // Update internal state so callers that rely on `allObjects` get the filtered list
                setAllObjects(results);
                setFilteredObjects(results);
                setSelectedRows(new Array(results.length).fill(false));

                return results;
            } catch (err: any) {
                setError(err?.message || 'Failed to load user objects');
                return [];
            } finally {
                setLoading(false);
            }
        },
        [loadAllObjects]
    );

    const getObjectsByUserId = useCallback(
        async (userId?: string | number, filterValue?: string): Promise<ObjectModel[]> => {
            setLoading(true);
            setError(null);

            const effectiveFilter =
                filterValue !== undefined ? filterValue.trim() : assetClassFilter.trim();
            setAssetClassFilter(effectiveFilter);

            try {
                let resolvedUserId = userId;

                

                if (resolvedUserId === null || resolvedUserId === undefined || String(resolvedUserId).trim() === '') {
                    const authTokenStr = localStorage.getItem('AUTH_TOKEN');
                    const authUser = authTokenStr ? JSON.parse(authTokenStr) : null;
                    resolvedUserId = authUser?.id ?? authUser?.user_id ?? authUser?.userId ?? null;

                    
                }

                if (resolvedUserId === null || resolvedUserId === undefined || String(resolvedUserId).trim() === '') {
                    throw new Error('Missing user id for object lookup');
                }

                

                const response = await fetch(`${API_BASE}/api/objects/get-objects-by-user-id`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: resolvedUserId }),
                });

                

                if (!response.ok) throw new Error('Failed to fetch objects for user');

                const result = await response.json();
                const objects: ObjectModel[] = Array.isArray(result)
                    ? result
                    : Array.isArray(result.data)
                    ? result.data
                    : [];

                const assetClassFilteredObjects = filterObjectsByAssetClass(objects, effectiveFilter);

                

                // DO NOT overwrite allObjects - this is only for permission checking
                // setAllObjects(assetClassFilteredObjects);
                // setFilteredObjects(assetClassFilteredObjects);
                // setSelectedRows(new Array(assetClassFilteredObjects.length).fill(false));

                return assetClassFilteredObjects;
            } catch (err: any) {
                setError(err.message || 'Failed to load objects for user');
                setAllObjects([]);
                setFilteredObjects([]);
                setSelectedRows([]);
                return [];
            } finally {
                setLoading(false);
            }
        },
        [assetClassFilter]
    );

    /**
     * Fetch the main image for an object (object_[id] with any extension)
     */
    const fetchObjectMainImage = useCallback(async (objectId: string | number) => {
        try {
            const response = await fetch(`${API_BASE}/api/objects/get-object-main-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ object_id: objectId }),
            });

            // Fallback for environments where /get-object-main-image is not deployed yet.
            if (response.status === 404) {
                const docsResponse = await fetch(`${API_BASE}/api/objects/get-object-docs`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ object_id: objectId }),
                });

                if (!docsResponse.ok) throw new Error('Failed to fetch object docs');

                const docsResult = await docsResponse.json();
                if (docsResult.success && Array.isArray(docsResult.data)) {
                    const pattern = new RegExp(`^object_${objectId}\\.`, 'i');
                    const mainImage = docsResult.data.find((doc: any) => pattern.test(doc?.name || ''));
                    return mainImage || null;
                }

                return null;
            }

            if (!response.ok) throw new Error('Failed to fetch object main image');

            const result = await response.json();
            if (result.success && Array.isArray(result.data) && result.data.length > 0) {
                return result.data[0];
            }

            return null;
        } catch (err: any) {
            return null;
        }
    }, []);

    

    /**
     * Fetch all docs for an object
     */
    const fetchObjectDocs = useCallback(async (objectId: string | number) => {
        try {
            const response = await fetch(`${API_BASE}/api/objects/get-object-docs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ object_id: objectId }),
            });

            if (!response.ok) throw new Error('Failed to fetch documents');

            const result = await response.json();
            if (result.success && Array.isArray(result.data)) {
                return result.data;
            }

            throw new Error(result.message || 'No documents found');
        } catch (err: any) {
            throw err;
        }
    }, []);

    /**
     * Search objects by name
     */
    const searchObjects = useCallback(
        (query: string = '') => {
            setSearchQuery(query);
            const results = allObjects.filter((item) => {
                const matchesSearch =
                    query === '' ||
                    (item.name?.toLowerCase() ?? '').includes(
                        query.toLowerCase()
                    );
                return matchesSearch;
            });

            setFilteredObjects(results);
            setSelectedRows(new Array(results.length).fill(false));
            
        },
        [allObjects]
    );

    /**
     * Legacy search method for backward compatibility
     */
    const filterItemsWithSearch = useCallback(
        (query: string = '') => {
            searchObjects(query);
        },
        [searchObjects]
    );

    /**
     * Sort objects by name
     */
    const sortByName = useCallback((ascending: boolean = true) => {
        setFilteredObjects((prev) => {
            const sorted = [...prev].sort((a, b) => {
                const nameA = (a.name ?? '').toLowerCase();
                const nameB = (b.name ?? '').toLowerCase();
                return ascending
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            });
            return sorted;
        });
    }, []);

    /**
     * Delete an object
     */
    const deleteObject = useCallback(
        async (item: ObjectModel): Promise<boolean> => {
            try {
                const res = await fetch(`${API_BASE}/api/objects/${item.id}`, {
                    method: 'DELETE',
                });

                if (!res.ok) throw new Error('Failed to delete object');

                // Reload objects after deletion
                await loadAllObjects();
                return true;
            } catch (err: any) {
                setError(err.message || 'Failed to delete object');
                return false;
            }
        },
        [loadAllObjects]
    );

    /**
     * Legacy delete method for backward compatibility
     */
    const deleteItem = useCallback(
        async (item: ObjectModel): Promise<boolean> => {
            return deleteObject(item);
        },
        [deleteObject]
    );

    /**
     * Create a new object
     */
    const createObject = useCallback(
        async (objectData: Partial<ObjectModel>): Promise<boolean> => {
            try {
                const res = await fetch(`${API_BASE}/api/objects`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(objectData),
                });

                if (!res.ok) throw new Error('Failed to create object');

                // Reload objects after creation
                await loadAllObjects();
                return true;
            } catch (err: any) {
                setError(err.message || 'Failed to create object');
                return false;
            }
        },
        [loadAllObjects]
    );

    /**
     * Create a new object using quick-object endpoint
     */
    const createQuickObject = useCallback(
        async (objectData: Partial<ObjectModel>): Promise<boolean> => {
            try {
                const name = String(objectData.name ?? '').trim();
                const address = String(objectData.address ?? objectData.street ?? '').trim();
                const description = String(objectData.description ?? '').trim();
                const assetclass = String(objectData.assetclass ?? '').trim();

                const query = new URLSearchParams({
                    name,
                    address,
                    description,
                    assetclass,
                });

                const res = await fetch(`${API_BASE}/api/objects/create-quick-object?${query.toString()}`, {
                    method: 'POST',
                });

                if (!res.ok) throw new Error('Failed to create object');

                // Reload objects after creation
                await loadAllObjects();
                return true;
            } catch (err: any) {
                setError(err.message || 'Failed to create quick object');
                return false;
            }
        },
        [loadAllObjects]
    );

    /**
     * Update an existing object
     */
    const updateObject = useCallback(
        async (id: string | number, objectData: Partial<ObjectModel>): Promise<boolean> => {
            try {
                const payload = {
                    id,
                    owner: objectData.owner,
                    company_id: objectData.company_id ?? objectData.company ?? null,
                    company_name: objectData.company_name,
                    name: objectData.name,
                    street: objectData.street ?? objectData.address,
                    zip_code: objectData.zip_code,
                    location: objectData.location,
                    description: objectData.description,
                    state: objectData.state,
                    status: objectData.status,
                    city: objectData.city,
                    country: objectData.country,
                    price: objectData.price,
                    currency: objectData.currency,
                    total_floors: objectData.Floors ?? objectData.floors,
                    total_units: objectData.units,
                    type: objectData.type_ ?? objectData.type,
                    img_url: objectData.img_url,
                    occupancy: objectData.occupancy,
                    zoning: objectData.zoning,
                    yield_net: objectData.yield_net,
                    yield_gross: objectData.yield_gross,
                    noi: objectData.NOI ?? objectData.noi,
                    cap_rate: objectData.cap_rate,
                    nda: objectData.nda === undefined ? 0 : (objectData.nda ? 1 : 0),
                };

                const res = await fetch(`${API_BASE}/api/objects/update-object`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                

                if (!res.ok) {
                    const errorText = await res.text();
                    throw new Error(errorText || 'Failed to update object');
                }

                // Reload objects after update
                await loadAllObjects();
                return true;
            } catch (err: any) {
                setError(err.message || 'Failed to update object');
                return false;
            }
        },
        [loadAllObjects]
    );

    /**
     * Create object media record from a dropped/selected file
     */
    const createObjectMedia = useCallback(
        async (objectId: string | number, file: File): Promise<boolean> => {
            try {
                const pickedFile = {
                    file,
                    name: file.name,
                    path: file.name,
                    bytes: await file.arrayBuffer(),
                };

                const success = await uploadDocumentToAzure(pickedFile, objectId);
                if (!success) {
                    throw new Error('Failed to create object media');
                }

                return success;
            } catch (err: any) {
                setError(err.message || 'Failed to create object media');
                return false;
            }
        },
        [uploadDocumentToAzure]
    );

    /**
     * Approve an object by id (calls backend `/approve-object`).
     * Updates local state to mark the object approved and removes it from
     * a pending-only filtered view if applicable.
     */
    const approveObject = useCallback(async (objectId: string | number, setApproved: boolean = true): Promise<boolean> => {
        if (!objectId) return false;

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(`${API_BASE}/api/objects/approve-object`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ object_id: objectId, approved: setApproved ? 1 : 0 }),
            });

            const result = await res.json().catch(() => ({}));
                if (!res.ok || !result.success) {
                const msg = result?.message || 'Failed to approve object';
                setError(msg);
                return false;
            }

            // Update objects in local state: set approved flag accordingly
            const approvedVal = setApproved ? 1 : 0;
            setAllObjects((prev) => prev.map((o) => (String(o.id) === String(objectId) ? { ...o, approved: approvedVal } : o)));

            setFilteredObjects((prev) => {
                // Detect if current view is a pending-only view (all items unapproved)
                const isPendingView = prev.every((item) => {
                    const v = item?.approved;
                    return v === false || v === 0 || (typeof v === 'string' && v.toLowerCase() === 'false');
                });

                if (isPendingView && setApproved) {
                    // remove the approved item from pending view when approving
                    return prev.filter((o) => String(o.id) !== String(objectId));
                }

                return prev.map((o) => (String(o.id) === String(objectId) ? { ...o, approved: approvedVal } : o));
            });

            return true;
        } catch (err: any) {
            setError(err?.message || 'Failed to approve object');
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Fetch all object types — returns raw DB rows so callers can access any field (e.g. description)
     */
    const fetchAllTypes = useCallback(async (): Promise<Record<string, any>[]> => {
        try {
            const res = await fetch(`${API_BASE}/api/objects/get-all-types`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json().catch(() => ({}));
            return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        } catch (err: any) {
            return [];
        }
    }, []);

    /**
     * Fetch all zoning values — returns raw DB rows
     */
    const fetchAllZonings = useCallback(async (): Promise<Record<string, any>[]> => {
        try {
            const res = await fetch(`${API_BASE}/api/objects/get-all-zonings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json().catch(() => ({}));
            return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        } catch (err: any) {
            return [];
        }
    }, []);

    /**
     * Fetch all occupancy values — returns raw DB rows
     */
    const fetchAllOccupancies = useCallback(async (): Promise<Record<string, any>[]> => {
        try {
            const res = await fetch(`${API_BASE}/api/objects/get-all-occupancies`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json().catch(() => ({}));
            return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        } catch (err: any) {
            return [];
        }
    }, []);

    /**
     * Fetch all status values — returns raw DB rows
     */
    const fetchAllStatus = useCallback(async (): Promise<Record<string, any>[]> => {
        try {
            const res = await fetch(`${API_BASE}/api/objects/get-all-status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = await res.json().catch(() => ({}));
            return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        } catch (err: any) {
            return [];
        }
    }, []);

    /**
     * Toggle row selection
     */
    const toggleRowSelection = useCallback((index: number) => {
        setSelectedRows((prev) => {
            const updated = [...prev];
            updated[index] = !updated[index];
            return updated;
        });
    }, []);

    /**
     * Select all rows
     */
    const selectAllRows = useCallback((select: boolean = true) => {
        setSelectedRows(new Array(filteredObjects.length).fill(select));
    }, [filteredObjects.length]);

    return {
        allObjects,
        filteredObjects,
        selectedRows,
        loading,
        error,
        currentUser,
        searchQuery,
        assetClassFilter,
        loadAllObjects,
        getObjectsByUserId,
        loadUserObjects,
        fetchObjectMainImage,
        fetchObjectDocs,
        searchObjects,
        filterItemsWithSearch,
        sortByName,
        createObject,
        createQuickObject,
        updateObject,
        createObjectMedia,
        deleteObject,
        deleteItem,
        toggleRowSelection,
        selectAllRows,
        fetchAllTypes,
        fetchAllZonings,
        fetchAllOccupancies,
        fetchAllStatus,
        loadPendingObjects,
        approveObject,
    };
}

export async function fetchObjectReturns(objectId: string | number): Promise<{ success: boolean; data: Array<{ my_row_id: number; object_id: number; date: string; return: number }>; message?: string }> {
    try {
        const res = await fetch(`${API_BASE}/api/objects/get-object-returns`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ object_id: objectId }),
        });
        return await res.json();
    } catch (error) {
        console.error('[fetchObjectReturns] Error:', error);
        return { success: false, data: [], message: 'Failed to fetch returns' };
    }
}

/**
 * Hook for handling fund returns CSV file uploads
 * Validates CSV format (must contain 'date' and 'return' columns)
 * Parses and returns structured data ready for backend submission
 */
export function useAddReturns(objectId?: string | number) {
    const [parsedData, setParsedData] = useState<Array<{ date: string; return: number; objectId?: string | number }> | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // fileInputRef removed (unused)

    const parseCSV = useCallback((fileContent: string): Array<{ date: string; return: number }> | null => {
        try {
            const lines = fileContent.trim().split('\n');
            if (lines.length < 2) {
                throw new Error('CSV file must contain header and at least one data row');
            }

            // Parse header
            const headerLine = lines[0];
            const headers = headerLine.split(',').map(h => h.trim().toLowerCase());
            const dateIndex = headers.findIndex(h => h === 'date');
            const returnIndex = headers.findIndex(h => h === 'return');

            if (dateIndex === -1 || returnIndex === -1) {
                throw new Error('CSV must contain "date" and "return" columns');
            }

            // Parse data rows
            const data: Array<{ date: string; return: number; objectId?: string | number }> = [];
            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue; // Skip empty lines

                const values = line.split(',').map(v => v.trim());
                const date = values[dateIndex];
                const returnVal = parseFloat(values[returnIndex]);

                if (!date || isNaN(returnVal)) {
                    throw new Error(`Invalid data at row ${i + 1}: date="${date}", return="${values[returnIndex]}"`);
                }

                data.push({ date, return: returnVal });
            }

            if (data.length === 0) {
                throw new Error('No valid data rows found in CSV');
            }

            return data;
        } catch (err: any) {
            throw new Error(`CSV Parse Error: ${err.message}`);
        }
    }, []);

    const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setLoading(true);
        setError(null);
        setParsedData(null);

        try {
            if (!file.name.endsWith('.csv')) {
                throw new Error('File must be a CSV file (.csv)');
            }

            if (!objectId) {
                throw new Error('Object ID is required to upload returns');
            }

            const fileContent = await file.text();
            const data = parseCSV(fileContent);

            console.log('[useAddReturns] Parsed CSV, submitting to backend', { rowCount: data?.length, objectId });

            const res = await fetch(`${API_BASE}/api/objects/add-object-returns`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    object_id: objectId,
                    rows: data!.map(({ date, return: ret }) => ({ date, return: ret })),
                }),
            });

            const result = await res.json();
            if (!result.success) {
                throw new Error(result.message || 'Backend returned an error');
            }

            // attach objectId to parsed rows when storing
            setParsedData(data ? data.map((r) => ({ ...r, objectId })) : null);
            console.log('[useAddReturns] Successfully uploaded returns', result);
        } catch (err: any) {
            const errorMsg = err?.message || 'Failed to upload CSV file';
            setError(errorMsg);
            console.error('[useAddReturns] Error:', errorMsg);
        } finally {
            setLoading(false);
            // Reset file input so same file can be re-selected
            event.target.value = '';
        }
    }, [parseCSV, objectId]);

    const triggerFileDialog = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        input.onchange = (e) => handleFileSelect(e as any);
        input.click();
    }, [handleFileSelect]);

    const clearData = useCallback(() => {
        setParsedData(null);
        setError(null);
    }, []);

    return {
        parsedData,
        loading,
        error,
        triggerFileDialog,
        clearData,
    };
}

export function useAddReturnsByRow(objectId?: string | number) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const addRow = useCallback(async (date: string, returnValue: number): Promise<boolean> => {
        if (!objectId) {
            setError('Object ID is required');
            return false;
        }
        setLoading(true);
        setError(null);
        setSuccess(false);
        try {
            const res = await fetch(`${API_BASE}/api/objects/add-object-returns`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    object_id: objectId,
                    rows: [{ date, return: returnValue }],
                }),
            });
            const result = await res.json();
            if (!result.success) {
                throw new Error(result.message || 'Backend returned an error');
            }
            setSuccess(true);
            return true;
        } catch (err: any) {
            setError(err?.message || 'Failed to add return');
            return false;
        } finally {
            setLoading(false);
        }
    }, [objectId]);

    const clearStatus = useCallback(() => {
        setError(null);
        setSuccess(false);
    }, []);

    return { addRow, loading, error, success, clearStatus };
}
